import { useState, useEffect, useRef, useCallback } from 'react';
import { Bot, Send, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

interface Message {
  type: 'customer' | 'bot';
  text: string;
  timestamp: string;
}

interface ChatAnimationProps {
  messages: Message[];
  title?: string;
  subtitle?: string;
  themeColor?: string;
  /** Render the full conversation immediately instead of animating it in.
   *  Used for the first paint so visitors see the product working without waiting. */
  instant?: boolean;
}

type MessageState = 'hidden' | 'typing' | 'visible';

const TypingIndicator = ({ themeColor }: { themeColor: string }) => (
  <div className="flex items-start gap-2.5">
    <div
      className={cn(
        "w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0",
        themeColor
      )}
    >
      <Bot className="w-3.5 h-3.5 text-white" />
    </div>
    <div className="bg-muted/80 px-4 py-2.5 rounded-2xl rounded-bl-md">
      <div className="flex items-center gap-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50"
            animate={{ y: [0, -4, 0] }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              delay: i * 0.15,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>
    </div>
  </div>
);

export const ChatAnimation = ({
  messages,
  title = "ChytBot Assistant",
  subtitle = "AI Agent",
  themeColor = "bg-primary",
  instant = false
}: ChatAnimationProps) => {
  const [messageStates, setMessageStates] = useState<MessageState[]>(
    () => messages.map(() => (instant ? 'visible' : 'hidden'))
  );
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [inputText, setInputText] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const addTimeout = useCallback((fn: () => void, delay: number) => {
    const id = setTimeout(fn, delay);
    timeoutsRef.current.push(id);
    return id;
  }, []);

  // Typewriter effect for input field
  const typeInInput = useCallback((text: string, startDelay: number, onComplete: () => void) => {
    const charDelay = 35;
    setIsInputFocused(true);

    for (let i = 0; i <= text.length; i++) {
      addTimeout(() => {
        setInputText(text.slice(0, i));
      }, startDelay + i * charDelay);
    }

    // After typing is done, brief pause then "send"
    addTimeout(() => {
      setInputText('');
      setIsInputFocused(false);
      onComplete();
    }, startDelay + text.length * charDelay + 300);
  }, [addTimeout]);

  useEffect(() => {
    clearTimeouts();
    setShowTypingIndicator(false);
    setInputText('');
    setIsInputFocused(false);

    if (instant) {
      setMessageStates(messages.map(() => 'visible'));
      return clearTimeouts;
    }
    setMessageStates(messages.map(() => 'hidden'));

    let t = 0;

    // Message 0: first customer message — appears quickly
    addTimeout(() => {
      setMessageStates(prev => { const n = [...prev]; n[0] = 'visible'; return n; });
    }, t += 300);

    // Message 1: first bot reply — typing indicator then appear
    addTimeout(() => setShowTypingIndicator(true), t += 500);
    addTimeout(() => {
      setShowTypingIndicator(false);
      setMessageStates(prev => { const n = [...prev]; n[1] = 'visible'; return n; });
    }, t += 800);

    // Message 2: second customer — typewriter in input then send
    const msg2Text = messages[2]?.text || '';
    const typewriterStart = t + 600;
    const typewriterDuration = msg2Text.length * 35 + 300;

    if (messages[2]) {
      typeInInput(msg2Text, typewriterStart, () => {
        setMessageStates(prev => { const n = [...prev]; n[2] = 'visible'; return n; });
      });
      t = typewriterStart + typewriterDuration;
    }

    // Message 3: second bot reply — typing indicator then appear
    if (messages[3]) {
      addTimeout(() => setShowTypingIndicator(true), t += 400);
      addTimeout(() => {
        setShowTypingIndicator(false);
        setMessageStates(prev => { const n = [...prev]; n[3] = 'visible'; return n; });
      }, t += 1000);
    }

    return clearTimeouts;
  }, [title, messages, instant, clearTimeouts, addTimeout, typeInInput]);

  const sendButtonActive = inputText.length > 0;

  return (
    <div className="relative w-full max-w-md mx-auto">
      {/* Device frame */}
      <div className="rounded-2xl bg-gradient-to-b from-border/80 to-border/40 p-px shadow-2xl shadow-black/8 dark:shadow-black/25">
        <div className="bg-card rounded-[15px] overflow-hidden flex flex-col h-[480px]">

          {/* Header */}
          <div className={cn("px-4 py-3.5 flex-shrink-0 relative", themeColor)}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-primary-foreground/15 backdrop-blur-sm rounded-xl flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm leading-tight line-clamp-1">{title}</h3>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                    <span className="text-white/70 text-[10px] font-semibold uppercase tracking-wider">
                      {subtitle}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((message, index) => {
              const isCustomer = message.type === 'customer';
              const state = messageStates[index];

              if (state === 'hidden') return null;

              return (
                <motion.div
                  key={`${title}-${index}`}
                  initial={{ opacity: 0, y: isCustomer ? 8 : 6, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{
                    duration: 0.25,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                  className={cn("flex items-end gap-2", isCustomer && "flex-row-reverse")}
                >
                  <div
                    className={cn(
                      "w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0",
                      isCustomer ? "bg-muted" : themeColor
                    )}
                  >
                    {isCustomer ? (
                      <User className="w-3.5 h-3.5 text-muted-foreground" />
                    ) : (
                      <Bot className="w-3.5 h-3.5 text-white" />
                    )}
                  </div>

                  <div className={cn("flex flex-col gap-1 max-w-[75%]", isCustomer ? "items-end" : "items-start")}>
                    <div
                      className={cn(
                        "px-3.5 py-2.5 text-[13px] leading-relaxed",
                        isCustomer
                          ? "bg-muted text-foreground rounded-2xl rounded-br-md"
                          : cn(themeColor, "text-white rounded-2xl rounded-bl-md")
                      )}
                    >
                      {message.text}
                    </div>
                    <span className="text-[10px] text-muted-foreground/70 px-1 tabular-nums">
                      {message.timestamp}
                    </span>
                  </div>
                </motion.div>
              );
            })}

            {/* Typing indicator */}
            <AnimatePresence>
              {showTypingIndicator && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  <TypingIndicator themeColor={themeColor} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Input area */}
          <div className="border-t border-border/60 px-3 py-3 bg-muted/20 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "flex-1 bg-background rounded-xl px-3.5 py-2 border text-sm min-h-[36px] flex items-center transition-all duration-200",
                  isInputFocused
                    ? "border-primary/50 ring-2 ring-primary/10"
                    : "border-border/60"
                )}
              >
                {inputText ? (
                  <span className="text-foreground">{inputText}<span className="inline-block w-px h-4 bg-foreground animate-pulse ml-px" /></span>
                ) : (
                  <span className="text-muted-foreground/60">Type a message...</span>
                )}
              </div>
              <motion.div
                className={cn("w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0", themeColor)}
                animate={{
                  opacity: sendButtonActive ? 1 : 0.35,
                  scale: sendButtonActive ? 1 : 0.95,
                }}
                transition={{ duration: 0.15 }}
              >
                <Send className="w-4 h-4 text-white" />
              </motion.div>
            </div>
            <div className="text-center mt-2">
              <span className="text-[9px] text-muted-foreground/50 uppercase tracking-[0.15em] font-semibold">
                Powered by{' '}
                <span className="text-primary/60">ChytBot</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
